import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

type IdTipo = number | string;

interface OpcoesGrid<T> {
  /** Lista exibida, na MESMA ordem da tela (ja filtrada/ordenada). */
  itens: T[];
  /** Identificador estavel de cada item (sobrevive a recarga da lista). */
  getId: (item: T) => IdTipo;
  /** Acao principal da linha: Enter ou duplo-clique. Ex.: abrir edicao/ficha. */
  onAtivar?: (item: T) => void;
  /** Liga/desliga a navegacao por teclado. Ex.: false durante selecao em lote. */
  ativo?: boolean;
  /**
   * Se o Enter dispara onAtivar. Default true.
   * Telas com acao irreversivel (Caixa: finalizar venda) passam false — a acao
   * fica so no botao/duplo-clique explicito, evitando disparo acidental por Enter.
   */
  ativarPorEnter?: boolean;
}

interface GridRowProps {
  'data-grid-row': IdTipo;
  'aria-selected': boolean;
  onClick: () => void;
  onDoubleClick?: () => void;
  style: CSSProperties;
}

interface RetornoGrid<T> {
  selectedId: IdTipo | null;
  setSelectedId: (id: IdTipo | null) => void;
  /** Espalhe em cada <tr>: selecao por clique, acao no duplo-clique, destaque e ancora de scroll. */
  rowProps: (item: T) => GridRowProps;
}

/**
 * Comportamento PADRAO de grid do sistema (extraido da lista de Produtos):
 * clicar marca a linha, ↑/↓ navegam, Enter/duplo-clique abrem, e a linha
 * selecionada acompanha o scroll. E a "forma" unica — cada tela so pluga o que
 * e seu (qual a acao principal, quando desligar o teclado).
 *
 * A selecao e guardada pela IDENTIDADE da linha (getId), nao pela posicao, entao
 * sobrevive a recarregamentos automaticos da lista (ex.: Caixa em tempo real).
 *
 * Convive com o "Enter como Tab" global (DA-011): o listener ignora quando o foco
 * esta num campo de texto/select, entao a busca da tela continua normal.
 */
export function useGridNavegavel<T>({
  itens,
  getId,
  onAtivar,
  ativo = true,
  ativarPorEnter = true,
}: OpcoesGrid<T>): RetornoGrid<T> {
  const [selectedId, setSelectedId] = useState<IdTipo | null>(null);

  // Mantem itens/callbacks atuais sem re-assinar o listener a cada render.
  const ref = useRef({ itens, getId, onAtivar, ativarPorEnter });
  ref.current = { itens, getId, onAtivar, ativarPorEnter };

  useEffect(() => {
    if (!ativo) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
      // Campo custom que ja tratou a tecla (o <ComboBox> fechado e uma <div>, entao nao cai no
      // filtro de tagName abaixo) — quem chamou preventDefault ja decidiu o que a tecla faz.
      if (e.defaultPrevented) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const { itens, getId, onAtivar, ativarPorEnter } = ref.current;
      if (itens.length === 0) return;
      const idx = selectedId == null ? -1 : itens.findIndex((it) => getId(it) === selectedId);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const alvo = itens[idx < 0 ? 0 : Math.min(idx + 1, itens.length - 1)];
        if (alvo) setSelectedId(getId(alvo));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const alvo = itens[idx < 0 ? 0 : Math.max(idx - 1, 0)];
        if (alvo) setSelectedId(getId(alvo));
      } else if (e.key === 'Enter' && ativarPorEnter && idx >= 0) {
        e.preventDefault();
        const alvo = itens[idx];
        if (alvo) onAtivar?.(alvo);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, ativo]);

  // A linha selecionada acompanha o scroll ao navegar com as setas.
  useEffect(() => {
    if (selectedId == null) return;
    document.querySelector(`[data-grid-row="${selectedId}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  function rowProps(item: T): GridRowProps {
    const id = getId(item);
    const selecionado = selectedId === id;
    const style: CSSProperties = {
      cursor: 'pointer',
      // Amarelo solido + letra preta: destaque daltonismo-safe que nao se confunde
      // com os tons de azul do tema (ver memoria user_daltonismo).
      ...(selecionado
        ? { background: '#fde68a', color: '#000', boxShadow: 'inset 3px 0 0 var(--accent)' }
        : {}),
    };
    return {
      'data-grid-row': id,
      'aria-selected': selecionado,
      onClick: () => setSelectedId(id),
      onDoubleClick: onAtivar ? () => onAtivar(item) : undefined,
      style,
    };
  }

  return { selectedId, setSelectedId, rowProps };
}
